import { useSelector, useDispatch } from 'react-redux';
import { useCallback } from 'react';
import axios from 'axios';
import { setCheckersPersist, toggleCheckerSelectionPersist } from '../store/actions';
import FormData from 'form-data';
import { storageKeys } from '../constants/storageKeys';
import { getAbortController } from '../utils/abortController';

/**
 * Custom hook for managing checkers.
 * Provides functionality to fetch, toggle selection , and invoke checkers.
 */
const useCheckerManager = () => {
  const checkers = useSelector((state) => state.checkers); 
  const dispatch = useDispatch();

  /**
   * Fetches the list of available checkers from the server and updates the Redux store.
   * The base URL for the checkers registry is retrieved from localStorage.
   * All discovered checkers are marked as selected by default.
   * 
   * @returns {Promise<Array>} - A promise that resolves to the array of discovered checkers.
   * @throws {Error} - Throws an error if fetching fails.
   */
  const fetchCheckers = useCallback(async () => {
    try {
      const baseUrl = JSON.parse(localStorage.getItem(storageKeys.CHECKERS_REGISTRY_BASEURL));
      const response = await axios.get(baseUrl);
      const discoveredCheckers = response.data.map((checker) => ({
        ...checker,
        selected: true, // Mark all checkers as selected by default
      }));
      dispatch(setCheckersPersist(discoveredCheckers)); // Store and persist
      return discoveredCheckers;
    } catch (error) {
      console.error('Error fetching checkers:', error);
      throw new Error('Failed to load checkers. Please check the base URL or server connection.');
    }
  }, [dispatch]);


  /**
   * Toggles the selection state of a checker by its ID.
   * Dispatches an action to update the selected state in the Redux store.
   *
   * @param {string} checkerId - The unique identifier of the checker to toggle.
   */
  const toggleCheckerSelectionState = (checkerId) => {
    dispatch(toggleCheckerSelectionPersist(checkerId)); 
  };

  
  /**
   * Invokes a single checker service with the provided BPMN XML and JSON data.
   * Sends a POST request to the checker's endpoint with the process data.
   *
   * @param {Object} checker - The checker object containing endpoint and metadata.
   * @param {string} xml - The BPMN XML string to be checked.
   * @param {Object} jsonBPMN - The BPMN JSON object to be checked.
   * @returns {Promise<Object>} - The response data from the checker service.
   * @throws {Error} - Throws an error if the checker invocation fails.
   */
  const invokeChecker = async (checker, xml, jsonBPMN) => {
    const abortController = getAbortController();

    // 🧩 Abort early if reset happened before the call
    if (abortController.signal.aborted) {
      console.warn(`Skipping checker "${checker.name}" — operation aborted before start.`);
      return null;
    }

    try {
      let data, headers;

      if (xml && !jsonBPMN) {
        // Only XML provided
        data = xml;
        headers = {
          'Content-Type': 'application/xml',
        };
      } else if (!xml && jsonBPMN) {
        // Only JSON provided
        data = JSON.stringify(jsonBPMN);
        headers = {
          'Content-Type': 'application/json',
        };
      } else {
        // Both XML and JSON provided, use FormData (multipart/form-data)
        const form = new FormData();
        if (xml) form.append('xml', xml);
        if (jsonBPMN) form.append('json', JSON.stringify(jsonBPMN));
        data = form;
        // For multipart/form-data, let the browser set the Content-Type including boundary
        headers = {};
      }

      const config = {
        method: 'post',
        url: checker.endpoint,
        headers,
        data,
        signal: abortController.signal,
      };
      const response = await axios(config);
      
      // 🧩 Abort-safe guard right after the request
      if (abortController.signal.aborted) {
        console.warn(`Checker "${checker.name}" completed after abort — ignoring result.`);
        return null;
      }
      return response.data;
    } catch (error) {
      if (axios.isCancel?.(error) || error.name === "CanceledError" || error.name === "AbortError") {
        console.warn(`Checker "${checker.name}" aborted.`);
        return null; // gracefully handle cancellation
      }
      console.error(`Error invoking checker "${checker.name}":`, error);
      throw new Error(`Failed to call the checker. Check the console for more details.`);
    }
  };


  /**
   * Invokes all selected checker services with the provided BPMN XML and JSON data.
   * Iterates over all checkers marked as selected, sends a POST request to each checker's endpoint,
   * and collects their responses or errors.
   *
   * @param {string} xml - The BPMN XML string to be checked.
   * @param {Object} jsonBPMN - The BPMN JSON object to be checked.
   * @returns {Promise<Array>} - An array of objects containing checker names and their responses or errors.
   * @throws {Error} - Throws an error if no checkers are selected.
   */
  const invokeAllSelectedCheckers = async (xml, jsonBPMN) => {

    const selectedCheckers = checkers.filter((checker) => checker.selected);

    if (selectedCheckers.length === 0) {
      throw new Error('No checkers selected.');
    }

    // Invoke all selected checkers and capture responses/errors
    const checkerResponses = await Promise.all(
      selectedCheckers.map(async (checker) => {
        try {
          const response = await invokeChecker(checker, xml, jsonBPMN);
          return { checkerName: checker.name, response };
        } catch (error) {
          return {
            checkerName: checker.name,
            error: error.message
          };
        }
      })
    );

  return checkerResponses;
  };

  // Expose checker manager functionality
  return {
    checkers,
    fetchCheckers,
    toggleCheckerSelectionState, 
    invokeAllSelectedCheckers, 
  };

};

export default useCheckerManager;
